import React from 'react';
import { Modal, Typography, IconButton, Tooltip, Box, Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

// Import extracted components and utilities
import TokenCard, { AvailableToken } from './TokenCard';
import TokenInputModal from './TokenInputModal';
import { AVAILABLE_TOKENS } from '../constants/tokenDefinitions';
import createModalStyles from '../styles/modalStyles';
import useIVRPromptModal  from '../hooks/useIVRPromptModal';

interface IVRPromptModalProps {
    isOpen: boolean;
    onClose: ()=> void;
    modalSubtitle: string;
    queueId: string;
    queueName: string;
    queueExtension: string;
    token: string;
    setRowData: (queueId: string, newPrompt: string) => void;
    lightMode: boolean;
}

const IVRPromptModal: React.FC<IVRPromptModalProps> = ({
  isOpen,
  onClose,
  modalSubtitle,
  queueId,
  queueExtension,
  queueName,
  token,
  setRowData,
  lightMode = false
}) => {
    const modalStyles = createModalStyles(lightMode);
    const [isTokensCollapsed, setIsTokensCollapsed] = React.useState(false);

    const {
      prompt,
      isLoading,
      showConfirmation,
      error,
      warning,
      isPlaying,
      showTokenInput,
      insertToken,
      handleTokenInputConfirm,
      handleTokenInputCancel,
      handleAutoplay,
      handlePromptChange,
      handleSubmit,
      handleConfirmSubmit,
      handleClose,
      handleConfirmationCancel
    } = useIVRPromptModal({
      isOpen,
      queueId,
      token,
      onClose,
      setRowData
    });

    return (
      <>
        {/* Main Modal */}
        <Modal open={isOpen} onClose={handleClose}>
          <div style={modalStyles.box} className="ivr-modal-box">
            {/* Header */}
            <div className="ivr-header" style={modalStyles.header}>
              <div>
                <Typography variant="h6">
                  {queueName} - ({queueExtension})
                </Typography>
                <Typography style={{ fontSize: "0.9em" }}>
                  {modalSubtitle}
                </Typography>
              </div>
              <IconButton onClick={handleClose} disabled={isLoading}>
                <CloseIcon />
              </IconButton>
            </div>

            {/* Content */}
            <div style={{ 
              flex: 1, 
              padding: "20px", 
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              minHeight: 0
            }}>
              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "20px",
                minHeight: 0,
                flex: 1
              }}>
                
                {/* Available Tokens Section */}
                <div style={{ 
                  backgroundColor: lightMode ? "#f5f5f5" : "#444", 
                  padding: "15px", 
                  borderRadius: "8px",
                  border: lightMode ? "1px solid #ddd" : "1px solid #555",
                  flexShrink: 0
                }}>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    marginBottom: "15px" 
                  }}>
                    <Typography variant="h6" style={{ color: lightMode ? "#333" : "#fff" }}>
                      📝 Available Dynamic Tokens
                    </Typography>
                    <IconButton
                      onClick={() => setIsTokensCollapsed(!isTokensCollapsed)}
                      size="small"
                      style={{ color: lightMode ? "#666" : "#ccc" }}
                    >
                      {isTokensCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                  </div>
                  
                  {!isTokensCollapsed && (
                    <>
                      <Typography variant="body2" style={{ marginBottom: "15px", color: lightMode ? "#666" : "#ccc" }}>
                        Click any token below to insert it into your prompt. These will be replaced with real-time data when the IVR plays.
                      </Typography>
                      
                      <div style={{ 
                        display: "flex", 
                        flexWrap: "wrap", 
                        gap: "12px",
                        justifyContent: "center"
                      }}>
                        {AVAILABLE_TOKENS.map((tokenItem: AvailableToken, index: number) => (
                          <div key={tokenItem.token} style={{ 
                            flex: "1 1 calc(50% - 6px)",
                            minWidth: "220px",
                            maxWidth: "calc(50% - 6px)"
                          }}>
                            <TokenCard
                              tokenItem={tokenItem}
                              lightMode={lightMode}
                              onTokenClick={insertToken}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Text Editor Section */}
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  gap: "10px", 
                  flexShrink: 0,
                  //margin: "5%",
                  marginRight: "5%",
                }}>
                  <Typography variant="h6" style={{ color: lightMode ? "#333" : "#fff", flexShrink: 0 }}>
                    ✏️ IVR Prompt Editor
                  </Typography>
                  
                  <textarea
                    id="ivr-prompt-textarea"
                    value={isLoading ? "Loading..." : prompt}
                    onChange={(e) => handlePromptChange(e.target.value)}
                    disabled={isLoading}
                    placeholder="Enter your IVR prompt text here... You can use the tokens above to make it dynamic!"
                    rows={10}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: lightMode ? "1px solid #ccc" : "1px solid #555",
                      borderRadius: "8px",
                      backgroundColor: lightMode ? "white" : "#444",
                      color: lightMode ? "black" : "white",
                      resize: "vertical",
                      fontFamily: "Arial, sans-serif",
                      fontSize: "14px",
                      lineHeight: "1.5",
                      flexShrink: 0
                    }}
                  />
                  
                  {/* Character count and info */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                    <Typography variant="caption" style={{ color: lightMode ? "#666" : "#ccc" }}>
                      {prompt.length} characters
                    </Typography>
                    <Tooltip title="Click tokens above to insert them into your prompt. All tokens must be properly formatted with valid parameters (1-60 minutes).">
                      <InfoIcon style={{ color: lightMode ? "#666" : "#ccc", fontSize: "16px" }} />
                    </Tooltip>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ 
                  display: "flex", 
                  justifyContent: "flex-end", 
                  alignItems: "center", 
                  gap: "15px", 
                  flexShrink: 0,
                  paddingTop: "20px",
                  paddingBottom: "20px",
                  borderTop: lightMode ? "1px solid #eee" : "1px solid #555",
                  marginTop: "auto"
                }}>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    {error && (
                      <Typography color="error" style={{ fontSize: "0.9em" }}>
                        {error}
                      </Typography>
                    )}
                    {warning && !error && (
                      <Typography style={{ 
                        fontSize: "0.9em", 
                        color: lightMode ? "#f57c00" : "#ffb74d" 
                      }}>
                        ⚠️ {warning}
                      </Typography>
                    )}
                    
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={isLoading || !prompt.trim()}
                      style={{ backgroundColor: lightMode ? "#1976d2" : "#64b5f6" }}
                    >
                      {isLoading ? 'Saving...' : 'Save Prompt'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {/* Confirmation Modal */}
        <Modal 
          open={showConfirmation}
          onClose={onClose}
        >
          <Box style={modalStyles.confirmationBox}>
            <div className="ivr-header" style={{ ...modalStyles.header, justifyContent: "start" }}>
              <WarningIcon style={{ paddingRight: 10, color: "yellow" }} />
              <Typography variant="h6">Save IVR Prompt?</Typography>
            </div>
            <div style={{ 
              padding: "20px", 
              display: "flex", 
              flexDirection: "column", 
              gap: "15px"
            }}>
              <Typography style={{ color: lightMode ? "#333" : "#fff" }}>
                Are you sure you want to save this IVR prompt? This will update the greeting that callers hear when they reach this queue.
              </Typography>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <Button
                  variant="outlined"
                  onClick={handleConfirmationCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Prompt'}
                </Button>
              </div>
            </div>
          </Box>
        </Modal>

        {/* Token Input Modal */}
        <TokenInputModal
          isOpen={showTokenInput}
          onClose={handleTokenInputCancel}
          onConfirm={handleTokenInputConfirm}
          lightMode={lightMode}
          modalStyles={modalStyles}
        />
      </>
    );
}

export default IVRPromptModal;